import React, {useState, useRef, useEffect} from 'react';
import {Stage, Layer, Line, Text, Image, Ellipse} from 'react-konva';
import AddTextButton from "../../buttons/PhotoEditor/AddTextButton";
import DrawButton from "../../buttons/PhotoEditor/DrawButton";
import UndoButton from "../../buttons/PhotoEditor/UndoButton";
import SaveImageButton from "../../buttons/PhotoEditor/SaveImageButton";
import ColorSelector from "../../buttons/PhotoEditor/ColorSelector";
import useContainerWidth from "./useContainerWidth";
import FontSizeSelector from "../../buttons/PhotoEditor/FontSizeSelector";
import LineWidthSelector from "../../buttons/PhotoEditor/LineWidthSelector";
import EllipseButton from "../../buttons/PhotoEditor/EllipseButton";

function PhotoEditor(props) {
    const containerRef = useRef(null);
    const containerWidth = useContainerWidth(containerRef);
    const [elements, setElements] = useState([]);
    const [history, setHistory] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [isDrawingMode, setIsDrawingMode] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const [drawingElement, setDrawingElement] = useState(null);
    const [color, setColor] = useState('#aa0000');
    const [fontSize, setFontSize] = useState(20);
    const [lineWidth, setLineWidth] = useState(5);
    const inputRef = useRef(null);
    const stageRef = useRef(null);

    const handleMouseDown = (e) => {
        if (!isDrawingMode || !e.target.getStage()) return;
        setIsDrawing(true);  // Start drawing
        const pos = e.target.getStage().getPointerPosition();
        if (drawingElement === 'line') {
            setElements(prev => [...prev, {
                id: elements.length,
                type: 'line',
                lineWidth: lineWidth,
                color: color,
                tool: 'pen',
                points: [pos.x, pos.y]
            }]);
        } else if (drawingElement === 'ellipse') {
            setElements(prev => [...prev, {
                id: elements.length,
                type: 'ellipse',
                radiusX: 0,
                radiusY: 0,
                x: pos.x,
                y: pos.y,
                fill: 'transparent',
                stroke: color,
                strokeWidth: lineWidth
            }]);
        }
        setHistory(prev => [...prev, elements]);
        if (editingId) {
            setEditingId(null); // Cancel editing if user clicks on canvas while editing text
        }
    };

    const handleMouseMove = (e) => {
        if (!isDrawing || elements.length === 0) return;  // Only draw if isDrawing is true
        const stage = e.target.getStage();
        const point = stage.getPointerPosition();
        const lastElement = elements[elements.length - 1];
        if (drawingElement === 'line') {
            const newElement = {
                ...lastElement,
                points: lastElement.points.concat([point.x, point.y])
            };
            const newElements = [...elements.slice(0, -1), newElement];
            setElements(newElements);
        } else if (drawingElement === 'ellipse') {
            const newElement = {
                ...lastElement,
                radiusX: Math.abs(lastElement.x - point.x),
                radiusY: Math.abs(lastElement.y - point.y)
            };
            const newElements = [...elements.slice(0, -1), newElement];
            setElements(newElements);
        }
    };

    const handleMouseUp = () => {
        setIsDrawing(false);  // Stop drawing
    };

    const addText = () => {
        if (isDrawingMode) setIsDrawingMode(false);
        const newText = {
            id: elements.length,
            type: 'text',
            color: color,
            text: '',
            x: 0,
            y: 0,
            fontSize: fontSize,
            draggable: true
        };
        setElements(prev => [...prev, newText]);
        setHistory(prev => [...prev, elements]);
        setEditingId(newText.id);
        inputRef.current.focus();
    };

    const handleDoubleClick = (id) => {
        setEditingId(id);
        inputRef.current.focus();
    };

    const handleChangeText = (e) => {
        const updatedElements = elements.map(el => {
            if (el.id === editingId) {
                return {...el, text: e.target.value};
            }
            return el;
        });
        setElements(updatedElements);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            setEditingId(null);
            if (inputRef.current) inputRef.current.blur();
        }
    };

    const undo = () => {
        if (history.length === 0) return;
        const prevElements = history[history.length - 1];
        setElements(prevElements);
        setHistory(prev => prev.slice(0, -1));
    };

    const handleDragStart = (elementIndex) => {
        // Capture the initial state at the start of the drag
        const elementToDrag = elements[elementIndex];
        setHistory(prevHistory => [...prevHistory, [...elements]]);
        // Optionally log or manage other state here
    };

    const handleDragEnd = (elementIndex, e) => {
        // Update the position of the element in the array
        const updatedElements = elements.map((el, idx) => {
            if (idx === elementIndex) {
                return {...el, x: e.target.x(), y: e.target.y()};
            }
            return el;
        });
        setElements(updatedElements);
    };

    const exportImage = () => {
        const dataURL = stageRef.current.toDataURL({pixelRatio: 1});
        fetch(dataURL)
            .then(res => res.blob()) // Convert dataURL to a blob
            .then(blob => {
                const file = new File([blob], "exported-image.png", {type: "image/png"});
                props.updateImage(props.data.index, file, dataURL);
            });
        setElements([]); // Clear the canvas
        setHistory([]); // Clear the history
        setEditingId(null); // Clear the editing ID
        setColor('#aa0000'); // Reset the color
        setIsDrawingMode(false); // Reset the drawing mode
        setIsDrawing(false); // Reset the drawing state
    };

    const changeColor = (event) => {
        setColor(event.target.value);
    };

    const changeFontSize = (event) => {
        setFontSize(event.target.value);
    }

    const changeLineWidth = (event) => {
        setLineWidth(event.target.value);
    }

    const getWidth = () => {
        if (!containerWidth) return null
        else if (containerWidth > props.data.image.naturalWidth) return props.data.image.naturalWidth;
        else return containerWidth;
    }

    const getHeight = () => {
        return getWidth() * (props.data.image.naturalHeight / props.data.image.naturalWidth);
    }

    function updateDrawing(element) {
        if (element === drawingElement) {
            setIsDrawingMode(!isDrawingMode);
        } else {
            setIsDrawingMode(true);
            setDrawingElement(element)
        }
    }

    return (
        <div ref={containerRef} style={{width: '100%', height: '100%'}}>
            <input
                ref={inputRef}
                style={{height: 0, width: 0, padding: 0, margin: 0, opacity: 0}}
                value={elements.find(el => el.id === editingId)?.text}
                onChange={handleChangeText}
                onBlur={() => setEditingId(null)}
                onKeyDown={handleKeyPress}
            />
            <div style={{marginBottom: "10px"}}>
                <ColorSelector value={color} onChange={changeColor}/>
                <FontSizeSelector value={fontSize} onChange={changeFontSize}/>
                <AddTextButton onClick={addText}/>
                <LineWidthSelector value={lineWidth} onChange={changeLineWidth}/>
                <DrawButton onClick={() => updateDrawing('line')}
                            isActive={isDrawingMode && drawingElement === 'line'}/>
                <EllipseButton onClick={() => updateDrawing('ellipse')}
                            isActive={isDrawingMode && drawingElement === 'ellipse'}/>
                <UndoButton onClick={undo}/>
                <SaveImageButton onClick={exportImage}/>
            </div>
            {getWidth() && (<Stage
                ref={stageRef}
                width={getWidth()}
                height={getHeight()}
                onMouseDown={handleMouseDown}
                onMousemove={handleMouseMove}
                onMouseup={handleMouseUp}
            >
                <Layer>
                    {props.data && <Image image={props.data.image} width={getWidth()} height={getHeight()}/>}
                    {elements.map((element, i) => (
                        element.type === 'line' ?
                            <Line
                                key={i}
                                points={element.points}
                                stroke={element.color}
                                strokeWidth={element.lineWidth}
                                tension={0.5}
                                lineCap="round"
                            /> : element.type === 'ellipse' ?
                                <Ellipse
                                    x={element.x}
                                    y={element.y}
                                    radiusX={element.radiusX}
                                    radiusY={element.radiusY}
                                    fill={element.fill}
                                    stroke={element.stroke}
                                    strokeWidth={element.strokeWidth}
                                /> :
                                <Text
                                    key={i}
                                    text={element.text}
                                    fill={element.color}
                                    x={element.x}
                                    y={element.y}
                                    fontSize={element.fontSize}
                                    draggable={true}
                                    onDragStart={() => handleDragStart(i)}
                                    onDragEnd={(e) => handleDragEnd(i, e)}
                                    onDblClick={() => handleDoubleClick(element.id)}
                                />
                    ))}
                </Layer>
            </Stage>)}
        </div>
    );
}

export default PhotoEditor;